// ABOUTME: Fires the contextual push-permission nudge (#2567) the first time
// a qualifying in-app event happens — a pending approval appears, or a
// background job finishes — instead of asking at launch/login. No-op on web
// and already-decided/already-offered installs (offerContextualPush is a
// one-shot check).

import { useEffect, useRef } from 'react'
import { usePendingApprovalsCount } from './usePendingApprovalsCount'
import { useJobsStore } from '@/stores/useJobsStore'
import { useUiStore } from '@/stores/useUiStore'
import { acceptContextualPush, offerContextualPush } from '@/platform/pushPrompt'

export function useContextualPushPrompt(): void {
  const showToast = useUiStore((state) => state.showToast)
  const pendingApprovalsCount = usePendingApprovalsCount()
  const hasActiveJobs = useJobsStore((state) => state.activeCount > 0)
  const wasActiveRef = useRef(false)

  const offer = () => {
    if (!offerContextualPush()) return
    showToast({
      type: 'info',
      title: 'Turn on notifications?',
      message: "We'll let you know when the team needs you or finishes something.",
      duration: 15_000,
      actionLabel: 'Enable',
      onAction: () => void acceptContextualPush(),
    })
  }

  // A pending approval is the agent asking for a decision — a real moment to
  // explain why notifications are useful.
  useEffect(() => {
    if (pendingApprovalsCount > 0) offer()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pendingApprovalsCount > 0])

  // A job finishing (active -> inactive) is the other qualifying moment the
  // AC calls out explicitly.
  useEffect(() => {
    if (wasActiveRef.current && !hasActiveJobs) offer()
    wasActiveRef.current = hasActiveJobs
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [hasActiveJobs])
}
